import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { ChartBar, Megaphone } from "@phosphor-icons/react";
import api from "@/lib/api";
import { CAMPAIGN_AUDIENCES } from "@/lib/constants";
import { PageHeader, SectionTitle, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function OutreachAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/outreach/analytics").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  if (data === false) return <EmptyState icon={ChartBar}>Could not load analytics.</EmptyState>;
  if (!data) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Crunching outreach numbers…</div>;

  const s = data.summary;

  return (
    <div className="space-y-6" data-testid="outreach-analytics-page">
      <PageHeader overline="AI Outreach" title="Outreach Analytics" subtitle="Is outreach actually translating into billable work?" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Recipients reached" value={s.recipients} testid="stat-a-recipients" />
        <Stat label="Response rate" value={`${s.response_rate}%`} tone="text-blue-600" testid="stat-a-response" />
        <Stat label="Conversion rate" value={`${s.conversion_rate}%`} tone="text-emerald-600" testid="stat-a-conversion" />
        <Stat label="Jobs won" value={s.converted} tone="text-accent" testid="stat-a-jobs" />
      </div>

      {data.campaigns.length === 0 ? (
        <EmptyState icon={Megaphone}>No campaigns to measure yet. <Link to="/app/outreach/new" className="text-accent hover:underline">Create one</Link> and results will appear here.</EmptyState>
      ) : (
        <>
          <Card className="p-6 border-border">
            <SectionTitle icon={ChartBar}>Campaign Outcomes</SectionTitle>
            <ResponsiveContainer width="100%" height={Math.max(220, data.campaigns.length * 46)}>
              <BarChart data={data.campaigns} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid horizontal={false} stroke="#E2E8F0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10 }} />
                <Tooltip cursor={{ fill: "rgba(10,37,64,0.05)" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="no_response" stackId="a" fill="#cbd5e1" name="No response" />
                <Bar dataKey="opened" stackId="a" fill="#3b82f6" name="Opened" />
                <Bar dataKey="replied" stackId="a" fill="#f59e0b" name="Replied" />
                <Bar dataKey="converted" stackId="a" fill="#059669" name="Converted" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="border-border overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-wide">Campaign</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide">Audience</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-right">Recipients</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-right">Response</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-right">Conversion</TableHead>
                    <TableHead className="text-xs uppercase tracking-wide text-right">Jobs Won</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.campaigns.map((c) => (
                    <TableRow key={c.id} className="hover:bg-muted/40" data-testid={`analytics-row-${c.id}`}>
                      <TableCell className="text-sm font-medium text-primary">{c.name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{CAMPAIGN_AUDIENCES[c.audience] || c.audience}</TableCell>
                      <TableCell className="text-right tabular text-sm">{c.recipients}</TableCell>
                      <TableCell className="text-right tabular text-sm">{c.response_rate}%</TableCell>
                      <TableCell className={`text-right tabular text-sm ${c.conversion_rate > 0 ? "text-emerald-600 font-semibold" : ""}`}>{c.conversion_rate}%</TableCell>
                      <TableCell className="text-right tabular text-sm">{c.converted}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
